import type { ReactNode } from 'react';
import { TbCompass, TbDroplet, TbLifebuoy, TbShip, TbTrash } from 'react-icons/tb';

import { PERMISSIONS, type Permission } from '@/permissions/permissions.catalog';

/**
 * The five books and where they hang off the Manage section.
 *
 * Shared rather than owned by the page, because the page is no longer the only
 * thing that has to know them: switching logs now lives in the Manage
 * breadcrumb, and the hub's Logs card links to the same five addresses. One list
 * means a book added here appears in all three by construction.
 */
export const LOGS_PATH = '/manage/logs';

export interface LogBookRoute {
  /** Route segment under the logs root. */
  path: string;
  label: string;
  /** The name on its own, for surfaces that have already said "log". */
  short: string;
  /**
   * The row name on the hub's Logs card, where `short` will not stand on its
   * own: "Daily" alone reads as an adjective. Falls back to `short`.
   */
  rowLabel?: string;
  /** What the book records, in one line. */
  subhead: string;
  icon: ReactNode;
  permission: Permission;
}

export const LOG_BOOKS: LogBookRoute[] = [
  {
    path: 'vessel-daily-log',
    label: 'Daily Log',
    short: 'Daily',
    rowLabel: 'Daily Log',
    subhead: 'Where the vessel lay each day, and who signed for it.',
    icon: <TbShip color="var(--color-breeze-teal)" />,
    permission: PERMISSIONS.logbook.DailyLog,
  },
  {
    path: 'daily-navigation-log',
    label: 'Daily Navigation Log',
    short: 'Navigation',
    subhead: 'Position, course and time: the passage as you ran it.',
    icon: <TbCompass color="var(--color-azure-pulse)" />,
    permission: PERMISSIONS.logbook.DigitalNavigationLog,
  },
  {
    path: 'trash-log',
    label: 'Trash Log',
    short: 'Trash',
    subhead: 'MARPOL Annex V: what you landed or discharged, and where.',
    icon: <TbTrash color="var(--color-warning)" />,
    permission: PERMISSIONS.logbook.TrashLog,
  },
  {
    path: 'safety-log',
    label: 'Safety Log',
    short: 'Safety',
    subhead: 'Equipment checks and safety rounds, with compliance remarks.',
    icon: <TbLifebuoy color="var(--color-error)" />,
    permission: PERMISSIONS.logbook.SafetyLog,
  },
  {
    path: 'oil-log',
    label: 'Oil Log',
    short: 'Oil',
    subhead: 'MARPOL Annex I: bunkering, fuel aboard and what it cost.',
    icon: <TbDroplet color="var(--color-warning)" />,
    permission: PERMISSIONS.logbook.OilLog,
  },
];

export const logBookPath = (book: LogBookRoute): string => `${LOGS_PATH}/${book.path}`;

/** The book a `/manage/logs/:log` segment names, if it names one at all. */
export const logBookFromSegment = (segment: string | undefined): LogBookRoute | undefined =>
  LOG_BOOKS.find((book) => book.path === segment);
